import { useQuery } from '@tanstack/react-query';
import {
  ACTIVE_REMEDIATION_STATUSES,
  type Engagement,
  type Observation,
  type Proof,
  type Remediation,
  type RemediationStatus,
} from '@ai-secure-twin/shared';
import { api } from '../../lib/api';

const formatInstant = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

/** The road a fix travels. Guardian moves it along; a person does the merging. */
const STEPS: { status: RemediationStatus; label: string }[] = [
  { status: 'PR_OPEN', label: 'Pull request opened' },
  { status: 'MERGED', label: 'Merged' },
  { status: 'DEPLOYED', label: 'Deployed' },
  { status: 'VERIFYING', label: 'Re-scanning' },
  { status: 'VERIFIED', label: 'Verified fixed' },
];
const STATUS_TEXT: Record<RemediationStatus, string> = {
  OPENING: 'Opening pull request',
  PR_OPEN: 'Waiting for review',
  MERGED: 'Merged — waiting for deploy',
  DEPLOYED: 'Deployed — re-scan next',
  VERIFYING: 'Re-scanning',
  VERIFIED: 'Verified fixed',
  STILL_PRESENT: 'Still present',
  INCONCLUSIVE: 'Inconclusive',
  CLOSED: 'Closed without merging',
  FAILED: 'Failed',
};

function statusClass(status: RemediationStatus): string {
  if (status === 'VERIFIED') return 'status status-verified';
  if (status === 'STILL_PRESENT' || status === 'FAILED') return 'status status-danger';
  if (ACTIVE_REMEDIATION_STATUSES.has(status)) return 'status status-waiting';
  return 'status status-done';
}

/** How far along the road a fix that left it (closed, failed, inconclusive) had got. */
function lastReached(r: Remediation): number {
  if (r.verifyScanId) return 3;
  if (r.deployment) return 2;
  if (r.pullRequest?.mergeCommitSha) return 1;
  return r.pullRequest ? 0 : -1;
}

function Steps({ remediation: r }: { remediation: Remediation }) {
  const onRoad = STEPS.findIndex((s) => s.status === r.status);
  const progress = r.status === 'OPENING' ? -1 : onRoad >= 0 ? onRoad : lastReached(r);
  return (
    <ol className="fix-steps" aria-label="Progress">
      {STEPS.map((step, i) => {
        const state =
          i < progress
            ? 'done'
            : i > progress
              ? 'todo'
              : r.status === 'VERIFIED'
                ? 'verified'
                : onRoad >= 0
                  ? 'current'
                  : 'done';
        return (
          <li key={step.status} data-state={state}>
            {step.label}
          </li>
        );
      })}
    </ol>
  );
}

const SHOWN_HEADERS = (r: Remediation): string[] =>
  (r.headers ?? []).map((h) => h.header.toLowerCase());

function ObservationView({
  label,
  side,
  observation,
  headers,
  fixed,
}: {
  label: string;
  side: 'before' | 'after';
  observation: Observation;
  headers: string[];
  fixed: boolean;
}) {
  return (
    <div className={`proof-side proof-side-${side}${fixed ? ' proof-fixed' : ''}`}>
      <h4>{label}</h4>
      <dl>
        <dt>Scan</dt>
        <dd>
          {observation.scanId.slice(0, 8)} · {formatInstant(observation.observedAt)}
        </dd>
        {observation.url ? (
          <>
            <dt>Request</dt>
            {/* Target-controlled: rendered as text, never as a link. */}
            <dd>
              {observation.method ?? 'GET'} {observation.url}
              {observation.status ? ` → ${observation.status}` : ''}
            </dd>
          </>
        ) : (
          <>
            <dt>Request</dt>
            <dd>Not reached by this scan</dd>
          </>
        )}
        {headers.map((name) => {
          const value = observation.headers?.[name];
          return (
            <span key={name} style={{ display: 'contents' }}>
              <dt>{name}</dt>
              <dd className={value ? 'proof-present' : observation.headers ? 'proof-absent' : ''}>
                {value ?? (observation.headers ? 'absent' : 'not recorded')}
              </dd>
            </span>
          );
        })}
        <dt>Rule fired</dt>
        <dd className={observation.ruleRaised ? 'proof-absent' : 'proof-present'}>
          {observation.ruleRaised ? 'yes' : 'no'}
        </dd>
        {observation.excerpt ? (
          <>
            <dt>Evidence</dt>
            <dd>{observation.excerpt.slice(0, 300)}</dd>
          </>
        ) : null}
      </dl>
    </div>
  );
}

function ProofView({ proof, remediation }: { proof: Proof; remediation: Remediation }) {
  const headers = SHOWN_HEADERS(remediation);
  const outcome =
    proof.outcome === 'FIXED'
      ? 'Fixed'
      : proof.outcome === 'STILL_PRESENT'
        ? 'Still present'
        : 'Not observed';
  return (
    <div style={{ marginTop: 12 }}>
      <div className="fix-head">
        <span style={{ fontSize: 13 }}>{proof.title}</span>
        <span
          className={
            proof.outcome === 'FIXED'
              ? 'status status-verified'
              : proof.outcome === 'STILL_PRESENT'
                ? 'status status-danger'
                : 'status status-done'
          }
        >
          {outcome}
        </span>
      </div>
      <div className="proof">
        <ObservationView
          label="Before"
          side="before"
          observation={proof.before}
          headers={headers}
          fixed={false}
        />
        <ObservationView
          label="After"
          side="after"
          observation={proof.after}
          headers={headers}
          fixed={proof.outcome === 'FIXED'}
        />
      </div>
      {proof.stillRaisedAt.length > 0 ? (
        <p className="meta">
          Still raised at:{' '}
          <span className="mono">{proof.stillRaisedAt.slice(0, 5).join(', ')}</span>
        </p>
      ) : null}
    </div>
  );
}

function RemediationCard({ remediation: r }: { remediation: Remediation }) {
  const kev = r.dependency?.kevCveIds ?? [];
  return (
    <article className={`fix${r.status === 'VERIFIED' ? ' fix-verified' : ''}`}>
      <div className="fix-head">
        <p className="fix-title">{r.title}</p>
        <span className={statusClass(r.status)}>{STATUS_TEXT[r.status]}</span>
      </div>
      <p className="meta">
        {r.kind === 'DEPENDENCY_UPGRADE' ? 'Dependency upgrade' : 'Security headers'} ·{' '}
        <span className="mono">
          {r.repository.owner}/{r.repository.repo}
        </span>{' '}
        · {r.changes.map((c) => c.summary).join('; ')}
        {kev.length > 0 ? (
          <>
            {' '}
            · <span className="prio prio-act-now">CISA KEV: {kev.join(', ')}</span>
          </>
        ) : null}
      </p>
      {r.pullRequest ? (
        <p className="meta">
          <a href={r.pullRequest.url} target="_blank" rel="noreferrer noopener">
            Pull request #{r.pullRequest.number}
          </a>
          {r.pullRequest.mergedAt ? ` · merged ${formatInstant(r.pullRequest.mergedAt)}` : ''}
          {r.deployment
            ? ` · ${r.deployment.sha.slice(0, 7)} live in ${r.deployment.environment} ${formatInstant(r.deployment.deployedAt)}`
            : ''}
        </p>
      ) : null}
      <Steps remediation={r} />
      {r.statusReason ? <p className="meta">{r.statusReason}</p> : null}
      {r.proofs.map((proof) => (
        <ProofView key={proof.findingId} proof={proof} remediation={r} />
      ))}
    </article>
  );
}

/**
 * Fixes Guardian proposed for this engagement: each pull request, where it is on its way to
 * production, and — once re-scanned — the before-and-after evidence that it worked.
 */
export function RemediationPanel({ engagement }: { engagement: Engagement }) {
  const remediations = useQuery({
    queryKey: ['remediations', engagement.id],
    queryFn: () => api.listRemediations(engagement.id),
    // Guardian moves fixes along on its own; keep the panel current while any is in flight.
    refetchInterval: (query) =>
      (query.state.data?.items ?? []).some((r) => ACTIVE_REMEDIATION_STATUSES.has(r.status))
        ? 15_000
        : false,
  });
  const items = remediations.data?.items ?? [];

  return (
    <section className="card panel" aria-labelledby="fixes-title">
      <div className="panel-head">
        <h2 id="fixes-title" className="panel-title">
          Fixes
        </h2>
        <span className="muted" style={{ fontSize: 12 }}>
          Proposed by Guardian · merged by people · proved by re-scan
        </span>
      </div>
      {remediations.isLoading ? (
        <p className="muted">Loading fixes…</p>
      ) : remediations.isError ? (
        <p className="muted">Fixes could not be loaded.</p>
      ) : items.length === 0 ? (
        <div className="empty">
          <p>No fixes proposed.</p>
          <p className="muted">
            With automatic remediation on for this application, Guardian opens a pull request for
            each finding it knows how to fix — a known-vulnerable library or a missing security
            header — and proves it with a re-scan once the change is deployed.
          </p>
        </div>
      ) : (
        <div className="fix-list">
          {items.map((r) => (
            <RemediationCard key={r.id} remediation={r} />
          ))}
        </div>
      )}
    </section>
  );
}
